/**
 * Custom element for the "Availability in store" drawer.
 *
 * Handles two concerns:
 *
 *  1. Variant update — when Horizon re-fetches the section after a swatch
 *     change, swaps this element's innerHTML from the new HTML by matching
 *     data-block-id. This bypasses morphdom, which intentionally skips open
 *     <dialog> elements to avoid closing them mid-update.
 *
 *  2. Show-more toggle — event-delegated click handler on this element so
 *     the listener survives innerHTML swaps without needing to be re-attached.
 */
class DevStoreAvailability extends HTMLElement {
  connectedCallback() {
    this._section = this.closest('.shopify-section');
    if (!this._section) return;
    this._section.addEventListener('variant:update', this._onVariantUpdate);
    this.addEventListener('click', this._onShowMoreClick);
  }

  disconnectedCallback() {
    if (!this._section) return;
    this._section.removeEventListener('variant:update', this._onVariantUpdate);
  }

  _onVariantUpdate = (/** @type {CustomEvent} */ event) => {
    const html = event.detail?.data?.html;
    if (!html) return;

    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (
      event.target instanceof HTMLElement &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    const updated = html.querySelector(
      `dev-store-availability[data-block-id="${this.dataset.blockId}"]`
    );
    if (!updated) return;

    this.innerHTML = updated.innerHTML;
  };

  _onShowMoreClick = (event) => {
    const btn = event.target.closest('[data-show-more]');
    if (!btn) return;

    const listId = btn.dataset.showMore;
    const list = this.querySelector(`[id="${listId}"]`);
    if (!list) return;

    const isExpanded = btn.getAttribute('aria-expanded') === 'true';

    if (!isExpanded) {
      list.style.maxHeight = list.scrollHeight + 'px';
    } else {
      // Pin to current visible height first so the transition has a defined start point
      list.style.maxHeight = list.getBoundingClientRect().height + 'px';
      list.offsetHeight; // force reflow
      list.style.maxHeight = '14rem';
    }

    btn.setAttribute('aria-expanded', !isExpanded ? 'true' : 'false');
    const nextLabel = !isExpanded
      ? (btn.dataset.showLessText || 'Show less')
      : (btn.dataset.showMoreText || 'Show more');
    const rollingLines = btn.querySelectorAll('[data-rolling-text-line]');
    if (rollingLines.length > 0) {
      rollingLines.forEach((line) => {
        line.textContent = nextLabel;
      });
    } else {
      btn.textContent = nextLabel;
    }
  };
}

if (!customElements.get('dev-store-availability')) {
  customElements.define('dev-store-availability', DevStoreAvailability);
}
